import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, ScrollView, ActivityIndicator, Image } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import ListsManager from '../services/ListsManager';
import { FaTwitter, FaTelegram, FaTiktok, FaInstagram, FaFacebookF, FaSearch, FaArrowLeft, FaUser, FaDownload, FaExternalLinkAlt, FaCheck, FaTimes, FaGlobe, FaPlus, FaNewspaper } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

// Custom SegmentedControl component
const SegmentedControl = ({ options, selectedIndex, onChange }) => {
  return (
    <View style={styles.segmentedControl}>
      {options.map((option, index) => (
        <TouchableOpacity
          key={index}
          style={{
            ...styles.segmentOption,
            ...(selectedIndex === index ? styles.segmentOptionSelected : {})
          }}
          onPress={() => onChange(index)}
        >
          {index === 0 ? (
            <FaTwitter style={{
              color: selectedIndex === index ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: 16,
              marginInlineEnd: 8
            }} />
          ) : index === 1 ? (
            <FaTelegram style={{
              color: selectedIndex === index ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: 16,
              marginInlineEnd: 8
            }} />
          ) : index === 2 ? (
            <FaTiktok style={{
              color: selectedIndex === index ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: 16,
              marginInlineEnd: 8
            }} />
          ) : index === 3 ? (
            <FaInstagram style={{
              color: selectedIndex === index ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: 16,
              marginInlineEnd: 8
            }} />
          ) : index === 4 ? (
            <FaFacebookF style={{
              color: selectedIndex === index ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: 16,
              marginInlineEnd: 8
            }} />
          ) : (
            <FaGlobe style={{
              color: selectedIndex === index ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: 16,
              marginInlineEnd: 8
            }} />
          )}
          <Text
            style={{
              ...styles.segmentOptionText,
              ...(selectedIndex === index ? styles.segmentOptionTextSelected : {})
            }}
          >
            {option}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
};

const UserItem = ({ user, onToggleFollow, isFollowing, sourceIndex }) => {
  const { t } = useTranslation();
  // Ensure user is a string and not null/undefined
  const userStr = typeof user === 'string' ? user : String(user || '');
  // const firstLetter = userStr.length > 0 ? userStr.charAt(0).toUpperCase() : '?';
  const userHandle = userStr.toLowerCase().replace(/\s/g, '');

  const handleViewProfile = () => {
    const url = sourceIndex === 0
      ? `https://x.com/${userStr}`
      : sourceIndex === 1
        ? `https://t.me/${userStr}`
        : sourceIndex === 2
          ? `https://www.tiktok.com/@${userStr}`
          : `https://www.instagram.com/${userStr}/`;
    window.open(url, '_blank');
  };

  return (
    <View style={styles.userItem}>
      <View style={styles.userInfo}>
        <View style={styles.avatar}>
          <FaUser style={styles.avatarIcon} />
        </View>
        <View>
          <Text style={styles.userName}>{userStr}</Text>
          <Text style={styles.userHandle}>@{userHandle}</Text>
        </View>
      </View>
      <View style={styles.userActions}>
        <TouchableOpacity
          style={styles.viewProfileButton}
          onPress={handleViewProfile}
        >
          <FaExternalLinkAlt style={styles.viewProfileIcon} />
          <Text style={styles.viewProfileText}>{t('app.followUser.viewProfile')}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={{
            ...styles.followButton,
            ...(isFollowing ? styles.followingButton : {})
          }}
          onPress={() => onToggleFollow(user)}
        >
          <Text style={{
            ...styles.followButtonText,
            ...(isFollowing ? styles.followingButtonText : {})
          }}>
            {isFollowing ? t('app.followUser.following') : t('app.followUser.follow')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

// Extract the real root domain from any feed URL (mirrors TweetCell's getRssSiteUrl).
// feeds.bbci.co.uk → bbci.co.uk   rcs.mako.co.il → mako.co.il   www.ynet.co.il → ynet.co.il
const getRootDomain = (url) => {
  if (!url) return null;
  try {
    const parts = new URL(url).hostname.split('.');
    const last = parts[parts.length - 1];
    const secondLast = parts[parts.length - 2] || '';
    const tldCount = last.length === 2 && secondLast.length <= 3 ? 2 : 1;
    return parts.slice(-(tldCount + 1)).join('.');
  } catch {
    return null;
  }
};

const RssFavicon = ({ url, size = 48 }) => {
  const [error, setError] = useState(false);
  const rootDomain = getRootDomain(url);
  const faviconUrl = rootDomain && !error
    ? `https://www.google.com/s2/favicons?domain=${rootDomain}&sz=64`
    : null;

  if (faviconUrl) {
    return (
      <Image
        source={{ uri: faviconUrl }}
        style={[styles.rssFaviconImage, { width: size, height: size, borderRadius: size * 0.25 }]}
        onError={() => setError(true)}
      />
    );
  }
  return (
    <View style={[styles.avatar, { width: size, height: size, borderRadius: size * 0.25 }]}>
      <FaGlobe style={{ ...styles.avatarIcon, color: 'var(--accent-primary)' }} />
    </View>
  );
};

const FollowNewUserScreen = ({ onClose, isModal, ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const state = location.state || {};

  const listID = props.listID || state.listID;
  const listName = props.listName || state.listName;
  const onUserAdded = props.onUserAdded || state.onUserAdded;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchTokens, setSearchTokens] = useState([]); // NEW: for the visual chips
  const [searchResults, setSearchResults] = useState([]);
  const [followingUsers, setFollowingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState({ type: null, message: '' }); // type: 'success' | 'error' | null
  const [sourceIndex, setSourceIndex] = useState(0); // 0: Twitter, 1: Telegram, 2: TikTok, 3: Instagram, 4: Facebook page, 5: RSS

  // Facebook page panel state
  const [fbPageInput, setFbPageInput] = useState('');
  const [fbAddLoading, setFbAddLoading] = useState(false);

  const instructionKeys = ['twitterInstructions', 'telegramInstructions', 'tiktokInstructions', 'instagramInstructions'];
  const instructions = t(`app.followUser.${instructionKeys[sourceIndex] || 'twitterInstructions'}`);

  // RSS-specific state
  const [rssFeeds, setRssFeeds] = useState([]);
  const [rssFollowing, setRssFollowing] = useState([]); // slugs followed for this list
  const [rssAddUrl, setRssAddUrl] = useState('');
  const [rssAddLoading, setRssAddLoading] = useState(false);
  const [rssCandidates, setRssCandidates] = useState([]); // feeds found for user to pick from
  const [rssAddingUrl, setRssAddingUrl] = useState(null); // feed_url currently being registered
  const [expandedDomains, setExpandedDomains] = useState({}); // domain → bool

  useEffect(() => {
    setSearchQuery('');
    setSearchTokens([]);
    setSearchResults([]);
    setError('');
    setRssAddUrl('');
    setRssCandidates([]);
    loadInitialData();
  }, [sourceIndex]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      if (sourceIndex === 5) {
        // RSS: load available feed registry + which feeds this list already follows
        const [feeds, follows] = await Promise.all([
          ListsManager.fetchRssFeeds(),
          listID ? ListsManager.getRssFollows(listID) : Promise.resolve([]),
        ]);
        setRssFeeds(feeds || []);
        setRssFollowing((follows || []).map(f => f.username));
        setLoading(false);
        return;
      }

      if (sourceIndex === 4) {
        // Facebook pages use a dedicated single-input panel — nothing to preload
        setLoading(false);
        return;
      }

      // Load existing follows
      let follows = [];
      if (sourceIndex === 0) {
        follows = await ListsManager.fetchAllTwitterUsernames();
      } else if (sourceIndex === 1) {
        follows = await ListsManager.fetchAllTelegramChannels();
      } else if (sourceIndex === 2) {
        follows = await ListsManager.fetchAllTikTokChannels();
      } else {
        follows = await ListsManager.fetchAllInstagramProfiles();
      }

      setFollowingUsers(follows || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading follows:', error);
      setError(t('app.followUser.failedLoadAccounts'));
      setLoading(false);
    }
  };

  const handleRssDiscover = async () => {
    if (!rssAddUrl.trim()) return;
    try {
      setRssAddLoading(true);
      setError('');
      setRssCandidates([]);
      const result = await ListsManager.discoverRssFeeds(rssAddUrl.trim());
      if (!result || !result.candidates || result.candidates.length === 0) {
        setError(t('app.followUser.noContentFound'));
        return;
      }
      if (result.candidates.length === 1) {
        // Fast-path: single feed — auto-add + follow
        const addResult = await ListsManager.addRssFeed(result.candidates[0].feed_url);
        if (addResult && addResult.success) {
          // Also follow it for this list
          if (listName && addResult.feed?.name) {
            await ListsManager.followRssFeed(addResult.feed.name, listName);
            setRssFollowing(prev => [...new Set([...prev, addResult.feed.name])]);
            if (onUserAdded) onUserAdded(addResult.feed.name, false);
          }
          setFeedback({ type: 'success', message: t('app.followUser.addedFeed', { name: addResult.feed.display_name }) });
          setRssAddUrl('');
          const feeds = await ListsManager.fetchRssFeeds();
          setRssFeeds(feeds || []);
          setTimeout(() => setFeedback({ type: null, message: '' }), 3000);
        } else {
          setError(t('app.followUser.noContentFound'));
        }
      } else {
        // Multiple feeds — show picker
        setRssCandidates(result.candidates);
      }
    } catch (err) {
      console.error('Error discovering RSS feeds:', err);
      setError(t('app.followUser.errorSearching'));
    } finally {
      setRssAddLoading(false);
    }
  };

  const handleRssCandidateAdd = async (candidate) => {
    setRssAddingUrl(candidate.feed_url);
    try {
      const result = await ListsManager.addRssFeed(candidate.feed_url);
      if (result && result.success) {
        // Also follow it for this list
        if (listName && result.feed?.name) {
          await ListsManager.followRssFeed(result.feed.name, listName);
          setRssFollowing(prev => [...new Set([...prev, result.feed.name])]);
          if (onUserAdded) onUserAdded(result.feed.name, false);
        }
        // Mark this candidate as added
        setRssCandidates(prev => prev.map(c =>
          c.feed_url === candidate.feed_url ? { ...c, _added: true } : c
        ));
        // Refresh available feeds list in background
        ListsManager.fetchRssFeeds().then(feeds => setRssFeeds(feeds || []));
      }
    } catch (err) {
      console.error('Error adding RSS feed:', err);
    } finally {
      setRssAddingUrl(null);
    }
  };

  const handleRssFollow = async (feedName) => {
    try {
      const success = await ListsManager.followRssFeed(feedName, listName);
      if (success) {
        setRssFollowing(prev => [...prev, feedName]);
        setFeedback({ type: 'success', message: t('app.followUser.followed') });
        if (onUserAdded) onUserAdded(feedName, false);
        setTimeout(() => setFeedback({ type: null, message: '' }), 2000);
      } else {
        setFeedback({ type: 'error', message: t('app.followUser.failedFollowFeed') });
        setTimeout(() => setFeedback({ type: null, message: '' }), 2000);
      }
    } catch (err) {
      console.error('Error following RSS feed:', err);
    }
  };

  const handleRssUnfollow = async (feedName) => {
    try {
      const success = await ListsManager.deleteRssFeed(listID, feedName);
      if (success) {
        setRssFollowing(prev => prev.filter(n => n !== feedName));
        setFeedback({ type: 'success', message: t('app.followUser.unfollowed') });
        setTimeout(() => setFeedback({ type: null, message: '' }), 2000);
      }
    } catch (err) {
      console.error('Error unfollowing RSS feed:', err);
    }
  };

  const handleFollowFacebookPage = async () => {
    const page = fbPageInput.trim();
    if (!page) return;
    try {
      setFbAddLoading(true);
      const success = await ListsManager.followFacebookPage(page, listName);
      if (success) {
        setFbPageInput('');
        setFeedback({ type: 'success', message: t('app.followUser.followed') });
        if (onUserAdded) onUserAdded(page, false);
        setTimeout(() => setFeedback({ type: null, message: '' }), 2000);
      } else {
        setFeedback({ type: 'error', message: t('app.followUser.failedFollow') });
        setTimeout(() => setFeedback({ type: null, message: '' }), 2000);
      }
    } catch (err) {
      console.error('Error following Facebook page:', err);
      setFeedback({ type: 'error', message: t('app.followUser.errorOccurred') });
      setTimeout(() => setFeedback({ type: null, message: '' }), 2000);
    } finally {
      setFbAddLoading(false);
    }
  };

  const validateAndExtractUsername = (input, sourceIdx) => {
    if (!input || input.trim().length === 0) {
      return null;
    }

    let username = input.trim().toLowerCase();

    // Source specific domains and regex
    const twitterDomains = ["x.com", "www.x.com", "twitter.com", "www.twitter.com", "t.co"];
    const telegramDomains = ["t.me", "www.t.me", "telegram.me", "www.telegram.me"];
    const tiktokDomains = ["tiktok.com", "www.tiktok.com"];
    const instagramDomains = ["instagram.com", "www.instagram.com"];

    const twitterRegex = /^[a-z0-9_]{1,15}$/;
    const telegramRegex = /^[a-z0-9_]{5,32}$/;
    const tiktokRegex = /^[a-z0-9._]{2,24}$/;
    const instagramRegex = /^[a-z0-9._]{1,30}$/;

    // Check if it's a URL - this allows auto-detecting the platform
    if (username.includes('.') && (username.includes('/') || username.startsWith('http'))) {
      try {
        const url = new URL(username.startsWith('http') ? username : `https://${username}`);
        
        let detectedIdx = sourceIdx;
        if (twitterDomains.some(d => url.hostname === d)) detectedIdx = 0;
        else if (telegramDomains.some(d => url.hostname === d)) detectedIdx = 1;
        else if (tiktokDomains.some(d => url.hostname === d)) detectedIdx = 2;
        else if (instagramDomains.some(d => url.hostname === d)) detectedIdx = 3;

        // Sync source index if a specific platform URL was pasted
        if (detectedIdx !== sourceIndex) {
          setSourceIndex(detectedIdx);
        }

        const pathParts = url.pathname.split('/').filter(Boolean);
        if (pathParts.length > 0) {
          let userPart = pathParts[0];
          if (detectedIdx === 2 || detectedIdx === 3) {
             // TikTok and Instagram can have /@username
             const atPart = pathParts.find(p => p.startsWith('@'));
             userPart = atPart ? atPart.substring(1) : pathParts[0];
          }
          username = userPart;

          // Validate extracted username
          const currentRegex = detectedIdx === 0 ? twitterRegex :
                              detectedIdx === 1 ? telegramRegex :
                              detectedIdx === 2 ? tiktokRegex : instagramRegex;
          if (currentRegex.test(username)) {
            return username;
          }
        }
      } catch (e) {
        // Not a valid URL, fallback to username check
      }
    }

    // Remove @ prefix if present
    if (username.startsWith('@')) {
      username = username.substring(1);
    }

    const regex = sourceIdx === 0 ? twitterRegex : sourceIdx === 1 ? telegramRegex : sourceIdx === 2 ? tiktokRegex : instagramRegex;

    // Basic validation
    if (!regex.test(username)) {
      return null;
    }

    return username;
  };

  useEffect(() => {
    const sourceIdx = sourceIndex;

    // Use tokens for either Twitter or Telegram
    let allInputTokens = [...searchTokens];
    if (searchQuery.trim()) {
      allInputTokens.push(...searchQuery.split(',').map(s => s.trim()).filter(Boolean));
    }

    if (allInputTokens.length === 0) {
      setError('');
      setSearchResults([]);
      return;
    }

    const validatedUsernames = [];
    const invalidInputs = [];

    for (const token of allInputTokens) {
      const username = validateAndExtractUsername(token, sourceIdx);
      if (username) {
        validatedUsernames.push(username);
      } else if (token) {
        invalidInputs.push(token);
      }
    }

    if (validatedUsernames.length === 0) {
      const sourceName = sourceIndex === 0 ? 'Twitter' : sourceIndex === 1 ? 'Telegram' : sourceIndex === 2 ? 'TikTok' : 'Instagram';
      setError(t('app.followUser.invalidUsername', { platform: sourceName }));
      setSearchResults([]);
      return;
    }

    if (invalidInputs.length > 0) {
      setError(t('app.followUser.someInvalid', { inputs: invalidInputs.join(', ') }));
    } else {
      setError('');
    }

    // De-duplicate array
    setSearchResults([...new Set(validatedUsernames)]);
  }, [searchTokens, searchQuery, sourceIndex]);

  const handleRemoveToken = (indexToRemove) => {
    setSearchTokens(searchTokens.filter((_, idx) => idx !== indexToRemove));
    setError('');
  };

  const handleFollowUser = async (usernameOrUsernames) => {
    try {
      setFollowLoading(true);
      setError('');

      const sourceIdx = sourceIndex;
      let success = false;

      // Call appropriate API
      if (sourceIdx === 0) {
        if (Array.isArray(usernameOrUsernames) && usernameOrUsernames.length > 1) {
          success = await ListsManager.followMultipleTwitterUsers(usernameOrUsernames, null, listName, listID);
        } else {
          const singleUsername = Array.isArray(usernameOrUsernames) ? usernameOrUsernames[0] : usernameOrUsernames;
          success = await ListsManager.followTwitterUser(singleUsername, null, listName, listID);
        }
      } else if (sourceIdx === 1) {
        // followTelegramChannel(channelUsername, listName)
        const singleUsername = Array.isArray(usernameOrUsernames) ? usernameOrUsernames[0] : usernameOrUsernames;
        success = await ListsManager.followTelegramChannel(singleUsername, listName);
      } else if (sourceIdx === 2) {
        if (Array.isArray(usernameOrUsernames) && usernameOrUsernames.length > 1) {
           // For multiple tiktoks, do sequential
           success = true;
           for (const uname of usernameOrUsernames) {
              const res = await ListsManager.followTikTokChannel(uname, listName);
              if (!res) success = false;
           }
        } else {
          const singleUsername = Array.isArray(usernameOrUsernames) ? usernameOrUsernames[0] : usernameOrUsernames;
          success = await ListsManager.followTikTokChannel(singleUsername, listName);
        }
      } else {
        // Instagram
        if (Array.isArray(usernameOrUsernames) && usernameOrUsernames.length > 1) {
           // For multiple instagrams, do sequential
           success = true;
           for (const uname of usernameOrUsernames) {
              const res = await ListsManager.followInstagramProfile(uname, listName);
              if (!res) success = false;
           }
        } else {
          const singleUsername = Array.isArray(usernameOrUsernames) ? usernameOrUsernames[0] : usernameOrUsernames;
          success = await ListsManager.followInstagramProfile(singleUsername, listName);
        }
      }

      if (success) {
        // Update local state
        const usersAdded = Array.isArray(usernameOrUsernames) ? usernameOrUsernames : [usernameOrUsernames];
        setFollowingUsers([...followingUsers, ...usersAdded]);

        // Trigger callback
        if (onUserAdded) {
          usersAdded.forEach(u => onUserAdded(u, sourceIdx === 0));
          // Show success feedback and stay on screen (don't close)
          setFeedback({ type: 'success', message: t('app.followUser.followed') });
          setSearchQuery('');
          setSearchTokens([]);
          setSearchResults([]);
          setTimeout(() => setFeedback({ type: null, message: '' }), 2000);
        } else if (onClose) {
          // If no onUserAdded handler, we might have to close to return result
          onClose({ newUsers: usersAdded, fromTwitter: sourceIdx === 0 });
        } else {
          // Standalone usage
          setFeedback({ type: 'success', message: t('app.followUser.followed') });
          setSearchQuery('');
          setSearchTokens([]);
          setSearchResults([]);
          setTimeout(() => setFeedback({ type: null, message: '' }), 2000);
        }
      } else {
        // Show error overlay
        setFeedback({ type: 'error', message: t('app.followUser.failedFollow') });
        setTimeout(() => setFeedback({ type: null, message: '' }), 2000);
      }
    } catch (error) {
      console.error('Error following user:', error);
      setFeedback({ type: 'error', message: t('app.followUser.errorOccurred') });
      setTimeout(() => setFeedback({ type: null, message: '' }), 2000);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleImport = () => {
    if (sourceIndex === 1) { // Telegram
      navigate('/phone-number', {
        state: {
          listID: listID,
          listName: listName
        }
      });
    } else {
      // Show web import functionality or appropriate message
      alert(t('app.followUser.twitterImportNotAvailable'));
    }
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  return (
    <View style={styles.container}>
      {!isModal && (
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <FaArrowLeft style={styles.backButtonIcon} />
            <Text style={styles.backButtonText}>{t('app.followUser.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {listID ? t('app.followUser.addToList', { listName: listName || 'List' }) : t('app.followUser.followAccount')}
          </Text>
          <View style={{ width: 50 }} />
        </View>
      )}

      <View style={styles.contentWrapper}>
        <ScrollView style={styles.content} contentContainerStyle={{ paddingBottom: 20 }}>
          <SegmentedControl
            options={['Twitter', 'Telegram', 'TikTok', 'Instagram', 'Facebook', 'Web']}
            selectedIndex={sourceIndex}
            onChange={setSourceIndex}
          />

          {sourceIndex === 4 ? (
            /* ── Facebook Page Panel ── */
            <View>
              <View style={styles.rssSectionHeader}>
                <FaFacebookF style={styles.rssSectionIcon} />
                <Text style={styles.rssSectionTitle}>{t('app.followUser.addFacebookPage', 'Add a Facebook page')}</Text>
              </View>
              <View style={styles.rssAddContainer}>
                <TextInput
                  style={styles.rssUrlInput}
                  placeholder={t('app.followUser.pasteFacebookPage', 'Page URL or @handle')}
                  placeholderTextColor="#888"
                  value={fbPageInput}
                  onChangeText={setFbPageInput}
                  autoCapitalize="none"
                  autoCorrect={false}
                  onSubmitEditing={handleFollowFacebookPage}
                />
                <TouchableOpacity
                  style={[styles.rssDiscoverButton, (fbAddLoading || !fbPageInput.trim()) && { opacity: 0.6 }]}
                  onPress={handleFollowFacebookPage}
                  disabled={fbAddLoading || !fbPageInput.trim()}
                >
                  {fbAddLoading ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <Text style={styles.rssDiscoverButtonText}>{t('app.followUser.follow')}</Text>
                  )}
                </TouchableOpacity>
              </View>
              <View style={styles.instructionsContainer}>
                <Text style={styles.instructionsText}>
                  {t('app.followUser.facebookInstructions', 'Paste a public Facebook page URL (e.g. https://www.facebook.com/humansofnewyork) or its @handle. We\'ll fetch its recent posts.')}
                </Text>
              </View>
            </View>
          ) : sourceIndex === 5 ? (
            /* ── Web Panel ── */
            <View>
              {/* Add new feed section */}
              <View style={styles.rssSectionHeader}>
                <FaPlus style={styles.rssSectionIcon} />
                <Text style={styles.rssSectionTitle}>{t('app.followUser.addWebsite')}</Text>
              </View>
              <View style={styles.rssAddContainer}>
                <TextInput
                  style={styles.rssUrlInput}
                  placeholder={t('app.followUser.pasteUrl')}
                  placeholderTextColor="#888"
                  value={rssAddUrl}
                  onChangeText={setRssAddUrl}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity
                  style={[styles.rssDiscoverButton, rssAddLoading && { opacity: 0.6 }]}
                  onPress={handleRssDiscover}
                  disabled={rssAddLoading}
                >
                  {rssAddLoading ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <Text style={styles.rssDiscoverButtonText}>{t('app.followUser.discover')}</Text>
                  )}
                </TouchableOpacity>
              </View>
              {rssAddLoading && (
                <Text style={styles.rssDiscoverHint}>{t('app.followUser.searchingContent')}</Text>
              )}

              {error ? <Text style={styles.errorText}>{error}</Text> : null}

              {/* Feed picker — shown when multiple candidates found */}
              {rssCandidates.length > 0 && (
                <View style={styles.rssCandidateContainer}>
                  <View style={styles.rssCandidateHeader}>
                    <Text style={styles.rssCandidateTitle}>
                      {t('app.followUser.foundFeeds', { count: rssCandidates.length })}
                    </Text>
                    <TouchableOpacity
                      onPress={async () => {
                        setRssCandidates([]);
                        setRssAddUrl('');
                        const feeds = await ListsManager.fetchRssFeeds();
                        setRssFeeds(feeds || []);
                      }}
                    >
                      <Text style={styles.rssCandidateDone}>{t('app.followUser.done')}</Text>
                    </TouchableOpacity>
                  </View>
                  {rssCandidates.map((candidate, idx) => (
                    <View key={idx} style={styles.rssCandidateRow}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.rssCandidateName}>{candidate.display_name}</Text>
                        {candidate.description ? (
                          <Text style={styles.rssCandidateDesc} numberOfLines={1}>{candidate.description}</Text>
                        ) : null}
                        <Text style={styles.rssCandidateMeta}>{t('app.followUser.itemsCount', { count: candidate.item_count, language: candidate.language })}</Text>
                        {candidate.sample_titles && candidate.sample_titles.length > 0 && (
                          <View style={styles.rssCandidateSamples}>
                            {candidate.sample_titles.map((title, i) => (
                              <Text key={i} style={styles.rssCandidateSampleItem} numberOfLines={1}>
                                · {title}
                              </Text>
                            ))}
                          </View>
                        )}
                      </View>
                      <TouchableOpacity
                        style={[styles.rssCandidateAddButton, candidate._added && styles.rssCandidateAddedButton]}
                        onPress={() => !candidate._added && handleRssCandidateAdd(candidate)}
                        disabled={candidate._added || rssAddingUrl === candidate.feed_url}
                      >
                        {rssAddingUrl === candidate.feed_url ? (
                          <ActivityIndicator size="small" color="white" />
                        ) : (
                          <Text style={styles.rssCandidateAddText}>{candidate._added ? t('app.followUser.added') : t('app.followUser.add')}</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}

              {/* Browse existing feeds */}
              <View style={styles.rssSectionHeader}>
                <FaNewspaper style={styles.rssSectionIcon} />
                <Text style={styles.rssSectionTitle}>{t('app.followUser.availableSources')}</Text>
              </View>

              {loading ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color="#4DA6FF" />
                  <Text style={styles.loadingText}>{t('app.followUser.loadingFeeds')}</Text>
                </View>
              ) : rssFeeds.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>{t('app.followUser.noSources')}</Text>
                </View>
              ) : (
                <View style={{ gap: 8 }}>
                  {(() => {
                    // Group feeds by root domain (bbci.co.uk, mako.co.il, etc.)
                    const groups = {};
                    rssFeeds.forEach(feed => {
                      const domain = getRootDomain(feed.url) || feed.url || 'other';
                      if (!groups[domain]) groups[domain] = [];
                      groups[domain].push(feed);
                    });

                    return Object.entries(groups).map(([domain, feeds]) => {
                      const isExpanded = !!expandedDomains[domain];
                      const followedCount = feeds.filter(f => rssFollowing.includes(f.name)).length;
                      const representativeUrl = feeds[0]?.url;
                      // Pretty label: first segment of root domain, capitalised
                      const domainLabel = domain.split('.')[0].replace(/-/g, ' ')
                        .replace(/\b\w/g, c => c.toUpperCase());

                      if (feeds.length === 1) {
                        // Single feed — show inline, no expand needed
                        const feed = feeds[0];
                        const isFollowing = rssFollowing.includes(feed.name);
                        return (
                          <View key={domain} style={[styles.resultsContainer, { paddingBottom: 0 }]}>
                            <View style={styles.userItem}>
                              <View style={styles.userInfo}>
                                <RssFavicon url={feed.url} />
                                <View style={{ flex: 1 }}>
                                  <Text style={styles.userName}>{feed.display_name || feed.name}</Text>
                                  {feed.description ? (
                                    <Text style={[styles.userHandle, { flexShrink: 1 }]} numberOfLines={1}>
                                      {feed.description}
                                    </Text>
                                  ) : null}
                                </View>
                              </View>
                              <TouchableOpacity
                                style={{ ...styles.followButton, ...(isFollowing ? styles.followingButton : {}) }}
                                onPress={() => isFollowing ? handleRssUnfollow(feed.name) : handleRssFollow(feed.name)}
                              >
                                <Text style={{ ...styles.followButtonText, ...(isFollowing ? styles.followingButtonText : {}) }}>
                                  {isFollowing ? t('app.followUser.following') : t('app.followUser.follow')}
                                </Text>
                              </TouchableOpacity>
                            </View>
                          </View>
                        );
                      }

                      // Multiple feeds — collapsible group
                      return (
                        <View key={domain} style={styles.rssGroupContainer}>
                          {/* Group header row */}
                          <TouchableOpacity
                            style={styles.rssGroupHeader}
                            onPress={() => setExpandedDomains(prev => ({ ...prev, [domain]: !prev[domain] }))}
                            activeOpacity={0.7}
                          >
                            <View style={styles.rssGroupHeaderLeft}>
                              <RssFavicon url={representativeUrl} />
                              <View>
                                <Text style={styles.rssGroupDomain}>{domainLabel}</Text>
                                <Text style={styles.rssGroupMeta}>
                                  {t('app.followUser.feedsCount', { count: feeds.length })}{followedCount > 0 ? ` · ${t('app.followUser.followingCount', { count: followedCount })}` : ''}
                                </Text>
                              </View>
                            </View>
                            <Text style={styles.rssGroupChevron}>{isExpanded ? '▲' : '▼'}</Text>
                          </TouchableOpacity>

                          {/* Expanded feed list */}
                          {isExpanded && feeds.map((feed, idx) => {
                            const isFollowing = rssFollowing.includes(feed.name);
                            return (
                              <View key={idx} style={[styles.userItem, styles.rssGroupFeedRow]}>
                                <View style={styles.userInfo}>
                                  <View style={{ flex: 1 }}>
                                    <Text style={styles.userName}>{feed.display_name || feed.name}</Text>
                                    {feed.description ? (
                                      <Text style={[styles.userHandle, { flexShrink: 1 }]} numberOfLines={1}>
                                        {feed.description}
                                      </Text>
                                    ) : null}
                                  </View>
                                </View>
                                <TouchableOpacity
                                  style={{ ...styles.followButton, ...(isFollowing ? styles.followingButton : {}) }}
                                  onPress={() => isFollowing ? handleRssUnfollow(feed.name) : handleRssFollow(feed.name)}
                                >
                                  <Text style={{ ...styles.followButtonText, ...(isFollowing ? styles.followingButtonText : {}) }}>
                                    {isFollowing ? t('app.followUser.following') : t('app.followUser.follow')}
                                  </Text>
                                </TouchableOpacity>
                              </View>
                            );
                          })}
                        </View>
                      );
                    });
                  })()}
                </View>
              )}
            </View>
          ) : (
          <>
            <View style={styles.searchContainer}>
              <View style={styles.searchInputWrapper}>
                <View style={styles.searchInputContainer}>
                  <FaSearch style={styles.searchIcon} />
                  <ScrollView
                    style={{ flex: 1, maxHeight: 120 }}
                    contentContainerStyle={styles.tokenContainer}
                  >
                    {searchTokens.map((token, idx) => (
                      <View key={idx} style={styles.tokenChip}>
                        <Text style={styles.tokenText}>{token}</Text>
                        <TouchableOpacity onPress={() => handleRemoveToken(idx)}>
                          <FaTimes style={styles.tokenCloseIcon} />
                        </TouchableOpacity>
                      </View>
                    ))}
                    <TextInput
                      style={styles.searchInput}
                      placeholder={
                        searchTokens.length > 0
                          ? t('app.followUser.addAnother')
                          : t('app.followUser.enterUsernameOrUrl', { platform: sourceIndex === 0 ? 'Twitter' : sourceIndex === 1 ? 'Telegram' : sourceIndex === 2 ? 'TikTok' : 'Instagram' })
                      }
                      placeholderTextColor="#888"
                      value={searchQuery}
                      onChangeText={(text) => {
                        if (text.includes(',')) {
                          const cleanToken = (tok) => tok.trim().replace(/[^a-zA-Z0-9_./:@]/g, '');
                          const tokens = text.split(',').map(cleanToken).filter(Boolean);
                          if (tokens.length > 0) {
                            if (text.endsWith(',') || tokens.length > 1) {
                              const newTokens = new Set([...searchTokens, ...tokens]);
                              setSearchTokens(Array.from(newTokens));
                              setSearchQuery('');
                            } else {
                              setSearchQuery(cleanToken(text));
                            }
                          } else {
                            setSearchQuery('');
                          }
                          setError('');
                          setSearchResults([]);
                        } else {
                          const cleanToken = (tok) => tok.replace(/[^a-zA-Z0-9_./:@]/g, '');
                          setSearchQuery(cleanToken(text));
                          setError('');
                          setSearchResults([]);
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.nativeEvent.key === 'Backspace' && searchQuery === '' && searchTokens.length > 0) {
                          handleRemoveToken(searchTokens.length - 1);
                        }
                      }}
                    />
                  </ScrollView>
                </View>
              </View>
            </View>

            <TouchableOpacity style={styles.importButton} onPress={handleImport}>
              <FaDownload style={styles.importButtonIcon} />
              <Text style={styles.importButtonText}>
                {t('app.followUser.importFrom', { platform: sourceIndex === 0 ? 'Twitter' : sourceIndex === 1 ? 'Telegram' : sourceIndex === 2 ? 'TikTok' : 'Instagram' })}
              </Text>
            </TouchableOpacity>

            <View style={styles.instructionsContainer}>
              <Text style={styles.instructionsText}>{instructions}</Text>
            </View>

            {error ? <Text style={styles.errorText}>{error}</Text> : null}

            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#4DA6FF" />
                <Text style={styles.loadingText}>{t('app.followUser.loading')}</Text>
              </View>
            ) : (
              <View style={[styles.resultsContainer, { paddingBottom: 0, flexDirection: 'column' }]}>
                {searchResults.length > 0 ? (
                  searchResults.map((user, index) => (
                    <UserItem
                      key={index}
                      user={user}
                      isFollowing={followingUsers.includes(user)}
                      onToggleFollow={() => handleFollowUser(user)}
                      sourceIndex={sourceIndex}
                    />
                  ))
                ) : (
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                      {t('app.followUser.typeUsername', { platform: sourceIndex === 0 ? 'Twitter' : sourceIndex === 1 ? 'Telegram' : sourceIndex === 2 ? 'TikTok' : 'Instagram' })}
                    </Text>
                  </View>
                )}

                {followLoading && (
                  <View style={styles.followLoadingContainer}>
                    <ActivityIndicator size="small" color="#4DA6FF" />
                    <Text style={styles.followLoadingText}>{t('app.followUser.followingUserLoader')}</Text>
                  </View>
                )}
              </View>
            )}
          </>
          )} {/* end of non-Web ternary */}
        </ScrollView>
      </View>

      {/* Floating Action Button strictly pinned to bottom container */}
      {searchResults.length > 0 && searchResults.length > 1 && !loading && (
        <View style={styles.floatingFollowContainer}>
          <TouchableOpacity
            style={styles.followAllButton}
            onPress={() => handleFollowUser(searchResults.filter(u => !followingUsers.includes(u)))}
          >
            <Text style={styles.followAllButtonText}>{t('app.followUser.followAll', { count: searchResults.length })}</Text>
          </TouchableOpacity>
        </View>
      )}
      {/* Feedback indicator */}
      {feedback.type && (
        <View style={[
          styles.feedbackOverlay,
          feedback.type === 'error' ? styles.feedbackOverlayError : styles.feedbackOverlaySuccess
        ]}>
          {feedback.type === 'success' ? (
            <FaCheck style={styles.feedbackIcon} />
          ) : (
            <FaTimes style={styles.feedbackIcon} />
          )}
          <Text style={styles.feedbackText}>{feedback.message}</Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'var(--bg-deep-void)',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.08)',
    backgroundColor: 'var(--bg-card)',
    zIndex: 10,
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(12px)',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s',
  },
  backButtonIcon: {
    color: 'var(--text-primary)',
    fontSize: 14,
    marginRight: 8,
  },
  backButtonText: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '600',
  },
  headerTitle: {
    color: 'var(--text-primary)',
    fontSize: 20,
    fontWeight: '700',
    letterSpacing: '-0.01em',
    fontFamily: 'Outfit, sans-serif',
    fontFamily: 'Outfit, sans-serif',
  },
  contentWrapper: {
    flex: 1,
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    padding: 24,
  },
  segmentedControl: {
    flexDirection: 'row',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 16,
    marginBottom: 24,
    padding: 6,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
  },
  segmentOption: {
    flex: 1,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
  },
  segmentOptionSelected: {
    backgroundColor: 'var(--accent-primary)',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
  segmentIcon: {
    fontSize: 16,
    color: 'var(--text-secondary)',
    marginRight: 8,
  },
  segmentIconSelected: {
    color: 'white',
  },
  segmentOptionText: {
    color: 'var(--text-secondary)',
    fontWeight: '600',
    fontSize: 15,
  },
  segmentOptionTextSelected: {
    color: 'white',
  },
  searchContainer: {
    flexDirection: 'row',
    marginBottom: 24,
    alignItems: 'flex-start', // allow flex-start in case tokens wrap
    gap: 12,
  },
  searchInputWrapper: {
    flex: 1,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 16,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
    minHeight: 52,
  },
  tokenContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    paddingVertical: 8,
    gap: 8,
  },
  floatingFollowContainer: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.05)',
    backgroundColor: 'var(--bg-card)',
  },
  followAllButton: {
    backgroundColor: 'var(--accent-primary)',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 24,
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.2s',
    alignItems: 'center',
    justifyContent: 'center',
  },
  followAllButtonText: {
    color: 'white',
    fontWeight: '700',
    fontSize: 16,
  },
  tokenChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.4)',
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  tokenText: {
    color: 'var(--text-primary)',
    fontSize: 14,
    marginRight: 6,
  },
  tokenCloseIcon: {
    color: 'var(--text-secondary)',
    fontSize: 12,
  },
  searchIcon: {
    color: 'var(--text-secondary)',
    fontSize: 16,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    minWidth: 150,
    paddingVertical: 8,
    color: 'var(--text-primary)',
    fontSize: 16,
    outlineStyle: 'none',
    backgroundColor: 'transparent',
    borderWidth: 0,
  },
  importButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 16,
    paddingVertical: 16,
    marginBottom: 24,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    flexDirection: 'row',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  importButtonIcon: {
    color: 'var(--accent-secondary)',
    fontSize: 16,
    marginRight: 12,
  },
  importButtonText: {
    color: 'var(--text-primary)',
    fontWeight: '600',
    fontSize: 16,
  },
  instructionsContainer: {
    backgroundColor: 'rgba(59, 130, 246, 0.05)',
    borderRadius: 20,
    padding: 20,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.15)',
    borderLeftWidth: 4,
    borderLeftColor: 'var(--accent-primary)',
  },
  instructionsText: {
    color: 'var(--text-primary)',
    lineHeight: 26,
    fontSize: 15,
  },
  errorText: {
    color: 'var(--accent-warning)',
    marginBottom: 16,
    padding: 16,
    backgroundColor: 'rgba(244, 63, 94, 0.1)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(244, 63, 94, 0.3)',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 40,
  },
  loadingText: {
    color: 'var(--text-secondary)',
    marginTop: 16,
    fontSize: 16,
  },
  resultsContainer: {
    flex: 1,
    borderRadius: 20,
    backgroundColor: 'var(--bg-card)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(12px)',
    overflow: 'hidden',
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    color: 'var(--text-muted)',
    textAlign: 'center',
    fontSize: 16,
    lineHeight: 24,
  },
  userItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
    transition: 'background-color 0.2s',
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  userActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.3)',
  },
  avatarIcon: {
    color: 'var(--accent-primary)',
    fontSize: 20,
  },
  userName: {
    color: 'var(--text-primary)',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  userHandle: {
    color: 'var(--text-secondary)',
    fontSize: 14,
  },
  viewProfileButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s',
  },
  viewProfileIcon: {
    color: 'var(--text-primary)',
    fontSize: 12,
    marginRight: 8,
  },
  viewProfileText: {
    color: 'var(--text-primary)',
    fontSize: 13,
    fontWeight: '600',
  },
  followButton: {
    backgroundColor: 'var(--accent-primary)',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 20,
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.2s',
  },
  followingButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: 'var(--accent-primary)',
    boxShadow: 'none',
  },
  followButtonText: {
    color: 'white',
    fontWeight: '700',
    fontSize: 14,
  },
  followingButtonText: {
    color: 'var(--accent-primary)',
  },
  followLoadingContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  followLoadingText: {
    color: 'var(--text-secondary)',
    marginLeft: 12,
    fontSize: 14,
  },
  feedbackOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: 24,
    paddingVertical: 24,
    paddingHorizontal: 32,
    zIndex: 999,
    backdropFilter: 'blur(10px)',
    borderWidth: 1,
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    minWidth: 180,
  },
  feedbackOverlaySuccess: {
    backgroundColor: 'rgba(16, 185, 129, 0.2)',
    borderColor: 'rgba(16, 185, 129, 0.4)',
    boxShadow: '0 0 30px rgba(16, 185, 129, 0.3)',
  },
  feedbackOverlayError: {
    backgroundColor: 'rgba(244, 63, 94, 0.2)',
    borderColor: 'rgba(244, 63, 94, 0.4)',
    boxShadow: '0 0 30px rgba(244, 63, 94, 0.3)',
  },
  feedbackIcon: {
    color: 'white',
    fontSize: 32,
    marginBottom: 8,
  },
  feedbackText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  rssGroupContainer: {
    borderRadius: 20,
    backgroundColor: 'var(--bg-card)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    overflow: 'hidden',
  },
  rssGroupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
  },
  rssGroupHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 14,
  },
  rssGroupDomain: {
    color: 'var(--text-primary)',
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 2,
  },
  rssGroupMeta: {
    color: 'var(--text-muted)',
    fontSize: 12,
  },
  rssGroupChevron: {
    color: 'var(--text-muted)',
    fontSize: 11,
    marginLeft: 8,
  },
  rssGroupFeedRow: {
    paddingLeft: 28,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.04)',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
  },
  rssFaviconImage: {
    width: 48,
    height: 48,
    borderRadius: 12,
    marginRight: 16,
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  rssSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 24,
    marginBottom: 12,
  },
  rssSectionIcon: {
    color: 'var(--accent-primary)',
    fontSize: 18,
    marginRight: 10,
  },
  rssSectionTitle: {
    color: 'var(--text-primary)',
    fontSize: 17,
    fontWeight: '700',
  },
  rssAddContainer: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 8,
  },
  rssUrlInput: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    color: 'var(--text-primary)',
    fontSize: 15,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    outlineStyle: 'none',
  },
  rssDiscoverButton: {
    backgroundColor: 'var(--accent-primary)',
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingVertical: 12,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 90,
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
  rssDiscoverButtonText: {
    color: 'white',
    fontWeight: '700',
    fontSize: 15,
  },
  rssDiscoverHint: {
    color: 'var(--text-secondary)',
    fontSize: 13,
    marginBottom: 8,
    fontStyle: 'italic',
  },
  rssCandidateContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    marginBottom: 20,
    overflow: 'hidden',
  },
  rssCandidateHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.08)',
  },
  rssCandidateTitle: {
    color: 'var(--text-secondary)',
    fontSize: 13,
    flex: 1,
    marginRight: 12,
  },
  rssCandidateDone: {
    color: 'var(--accent-primary)',
    fontSize: 14,
    fontWeight: '600',
  },
  rssCandidateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
    gap: 12,
  },
  rssCandidateName: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  rssCandidateDesc: {
    color: 'var(--text-muted)',
    fontSize: 12,
    marginBottom: 2,
  },
  rssCandidateMeta: {
    color: 'var(--text-muted)',
    fontSize: 11,
    opacity: 0.7,
  },
  rssCandidateAddButton: {
    backgroundColor: 'var(--accent-primary)',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 7,
    minWidth: 60,
    alignItems: 'center',
  },
  rssCandidateAddedButton: {
    backgroundColor: 'rgba(39, 174, 96, 0.25)',
    borderWidth: 1,
    borderColor: 'rgba(39, 174, 96, 0.5)',
  },
  rssCandidateAddText: {
    color: 'white',
    fontSize: 13,
    fontWeight: '600',
  },
  rssCandidateSamples: {
    marginTop: 6,
    borderLeftWidth: 2,
    borderLeftColor: 'rgba(255, 255, 255, 0.08)',
    paddingLeft: 8,
  },
  rssCandidateSampleItem: {
    color: 'var(--text-secondary)',
    fontSize: 11,
    lineHeight: 18,
    opacity: 0.85,
  },
});

export default FollowNewUserScreen; 